import type {
  AgentBlueprint,
  AgentBlueprintReasoningEffort,
} from "../../generated/core-api";

export const MAX_STOP_SEQUENCES = 4;
export const MAX_STOP_SEQUENCE_LENGTH = 64;
export const MAX_TIMEOUT_SECONDS = 180;

export const REASONING_EFFORT_OPTIONS: {
  value: Exclude<AgentBlueprintReasoningEffort, null>;
  label: string;
}[] = [
  { value: "minimal", label: "Minimal" },
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "Extra high" },
];

export type ModelParamIssue = {
  field: string;
  message: string;
};

export function emptyAgentBlueprint(modelName: string): AgentBlueprint {
  return {
    name: "",
    description: "",
    system_prompt_template: "",
    model_name: modelName,
    version: "1.0.0",
  };
}

export function parseOptionalNumber(raw: string): number | undefined {
  if (raw.trim() === "") {
    return undefined;
  }
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
}

export function parseStopSequences(raw: string): string[] | undefined {
  const sequences = raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0);
  return sequences.length > 0 ? sequences : undefined;
}

export function formatStopSequences(
  sequences: string[] | null | undefined,
): string {
  return sequences?.join("\n") ?? "";
}

export function parseOptionalBoolean(
  raw: string,
): boolean | undefined {
  if (raw === "true") {
    return true;
  }
  if (raw === "false") {
    return false;
  }
  return undefined;
}

export function parseReasoningEffort(
  raw: string,
): AgentBlueprintReasoningEffort | undefined {
  return (
    REASONING_EFFORT_OPTIONS.find((option) => option.value === raw)?.value ??
    undefined
  );
}

function isSet(value: unknown): boolean {
  return value !== undefined && value !== null;
}

function outOfRange(
  value: number,
  min: number,
  max: number,
  integer = false,
): boolean {
  if (integer && !Number.isInteger(value)) {
    return true;
  }
  return value < min || value > max;
}

export function getBlueprintModelParamIssues(
  blueprint: AgentBlueprint,
): ModelParamIssue[] {
  const issues: ModelParamIssue[] = [];

  if (isSet(blueprint.temperature)) {
    const value = blueprint.temperature as number;
    if (outOfRange(value, 0, 2)) {
      issues.push({
        field: "temperature",
        message: "Temperature must be between 0 and 2.",
      });
    }
  }

  if (isSet(blueprint.top_p)) {
    const value = blueprint.top_p as number;
    if (outOfRange(value, 0, 1)) {
      issues.push({
        field: "top_p",
        message: "top_p must be between 0 and 1.",
      });
    }
  }

  if (isSet(blueprint.max_tokens)) {
    const value = blueprint.max_tokens as number;
    if (!Number.isInteger(value) || value < 1) {
      issues.push({
        field: "max_tokens",
        message: "Max tokens must be a whole number of at least 1.",
      });
    }
  }

  if (isSet(blueprint.frequency_penalty)) {
    const value = blueprint.frequency_penalty as number;
    if (outOfRange(value, -2, 2)) {
      issues.push({
        field: "frequency_penalty",
        message: "Frequency penalty must be between -2 and 2.",
      });
    }
  }

  if (isSet(blueprint.presence_penalty)) {
    const value = blueprint.presence_penalty as number;
    if (outOfRange(value, -2, 2)) {
      issues.push({
        field: "presence_penalty",
        message: "Presence penalty must be between -2 and 2.",
      });
    }
  }

  if (isSet(blueprint.seed) && !Number.isInteger(blueprint.seed as number)) {
    issues.push({
      field: "seed",
      message: "Seed must be a whole number.",
    });
  }

  if (isSet(blueprint.timeout)) {
    const value = blueprint.timeout as number;
    if (value <= 0 || value > MAX_TIMEOUT_SECONDS) {
      issues.push({
        field: "timeout",
        message: `Timeout must be greater than 0 and at most ${MAX_TIMEOUT_SECONDS} seconds.`,
      });
    }
  }

  if (blueprint.stop_sequences) {
    if (blueprint.stop_sequences.length > MAX_STOP_SEQUENCES) {
      issues.push({
        field: "stop_sequences",
        message: `At most ${MAX_STOP_SEQUENCES} stop sequences are allowed.`,
      });
    }
    if (
      blueprint.stop_sequences.some(
        (sequence) => sequence.length > MAX_STOP_SEQUENCE_LENGTH,
      )
    ) {
      issues.push({
        field: "stop_sequences",
        message: `Each stop sequence can be at most ${MAX_STOP_SEQUENCE_LENGTH} characters.`,
      });
    }
  }

  return issues;
}

export function areBlueprintModelParamsValid(
  blueprint: AgentBlueprint,
): boolean {
  return getBlueprintModelParamIssues(blueprint).length === 0;
}

export function usesGpt5FamilyModel(modelName: string): boolean {
  return modelName.toLowerCase().includes("gpt-5");
}

export function getBlueprintModelParamBadges(
  blueprint: AgentBlueprint,
): string[] {
  const badges: string[] = [];

  if (isSet(blueprint.reasoning_effort)) {
    badges.push(`effort ${blueprint.reasoning_effort}`);
  }
  if (isSet(blueprint.temperature)) {
    badges.push(`temp ${blueprint.temperature}`);
  }
  if (isSet(blueprint.top_p)) {
    badges.push(`top_p ${blueprint.top_p}`);
  }
  if (isSet(blueprint.max_tokens)) {
    badges.push(`max ${blueprint.max_tokens}`);
  }
  if (isSet(blueprint.frequency_penalty)) {
    badges.push(`freq ${blueprint.frequency_penalty}`);
  }
  if (isSet(blueprint.presence_penalty)) {
    badges.push(`pres ${blueprint.presence_penalty}`);
  }
  if (isSet(blueprint.seed)) {
    badges.push(`seed ${blueprint.seed}`);
  }
  if (blueprint.stop_sequences && blueprint.stop_sequences.length > 0) {
    badges.push(
      blueprint.stop_sequences.length === 1
        ? `stop ${blueprint.stop_sequences[0]}`
        : `${blueprint.stop_sequences.length} stops`,
    );
  }
  if (blueprint.parallel_tool_calls === true) {
    badges.push("parallel tools");
  } else if (blueprint.parallel_tool_calls === false) {
    badges.push("no parallel tools");
  }
  if (isSet(blueprint.timeout)) {
    badges.push(`timeout ${blueprint.timeout}s`);
  }

  return badges;
}
